import React, { useEffect, useState, useRef, useContext } from "react";
import "./forecasting.css";
import { IoMdArrowDropdown, IoMdArrowDropup, IoMdPlayCircle } from "react-icons/io";
import { FaChartBar } from "react-icons/fa";
import { FaTableCells } from "react-icons/fa6";
import { MdOutlineRefresh } from "react-icons/md";
import Table from "./table";
import Charts from "./charts";
import * as d3 from "d3";
import { ForecastContext } from "./ForecastContext";
import { MdOutlineKeyboardArrowUp } from "react-icons/md";
import {
    forecastApis, commitForecastApis, forecastRangeApis, mrRateApis, postMRRates, fetchLoadModel,
    fetchWindModel, fetchSolarModel, fetchPriceModel
} from './api';
import { MdCloudUpload } from "react-icons/md";
import { MdOutlineEdit } from "react-icons/md";
import { GoPlusCircle } from "react-icons/go";
import { MdDeleteForever } from "react-icons/md";
import { AiOutlineFullscreenExit } from "react-icons/ai";
import { IoMdSettings } from "react-icons/io";
import { FaChartLine } from "react-icons/fa6";
import StackedAreaChart from "./stackedAreaChart";
import { AiOutlineAreaChart } from "react-icons/ai";

const Forecasting = ({ forecastType, date, onExit }) => {
    const {
        setLoadForecastData,
        setPriceForecastData,
        setSolarForecastData,
        setWindForecastData
    } = useContext(ForecastContext);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [selectedOption, setSelectedOption] = useState(forecastType || "Load Forecast");
    const [selectedModel, setSelectedModel] = useState("NN_NETWORK");
    const [showTable, setShowTable] = useState(false);
    const [forecastData, setForecastData] = useState(null);
    const [headers, setHeaders] = useState([]);
    const [tableData, setTableData] = useState([]);
    const dropdownRef = useRef(null);
    const tableRef = useRef(null);
    const chartRef = useRef(null);
    const [xData, setXData] = useState([]);
    const [yData, setYData] = useState([]);
    const options = ["Load Forecast", "Price Forecast", "Solar Forecast", "Wind Forecast"];
    const [isOpen, setIsOpen] = useState(false);
    const [timeRange, setTimeRange] = useState("0D");
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [showButtons, setShowButtons] = useState(false);
    const [rows, setRows] = useState([]);
    const [seriesName, setSeriesName] = useState("");
    const [fromBlock, setFromBlock] = useState("");
    const [toBlock, setToBlock] = useState("");
    const [weight, setWeight] = useState("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const defaultMRRates = {
        "Load Forecast": 0.2,
        "Price Forecast": 0.2,
        "Solar Forecast": 0.2,
        "Wind Forecast": 0.2
    };
    const [selectedLoadModel, setSelectedLoadModel] = useState(() => localStorage.getItem("selectedLoadModel") || "");
    const [selectedPriceModel, setSelectedPriceModel] = useState(() => localStorage.getItem("selectedPriceModel") || "");
    const [selectedSolarModel, setSelectedSolarModel] = useState(() => localStorage.getItem("selectedSolarModel") || "");
    const [selectedWindModel, setSelectedWindModel] = useState(() => localStorage.getItem("selectedWindModel") || "");
    const [loadModels, setLoadModels] = useState([]);
    const [priceModels, setPriceModels] = useState([]);
    const [solarModels, setSolarModels] = useState([]);
    const [windModels, setWindModels] = useState([]);
    const [mrRate, setMrRate] = useState(() => {
        const storedRates = JSON.parse(localStorage.getItem("mrRates")) || {};
        return storedRates["Load Forecast"] ?? 0.2;
    });
    const [debounceTimer, setDebounceTimer] = useState(null);
    const [showCommitButton, setShowCommitButton] = useState(true);
    const [rtForecastRow, setRtForecastRow] = useState([]);
    const [rtForecastHeaders, setRtForecastHeaders] = useState([]);
    const [summaryTable, setSummaryTable] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isStackedView, setIsStackedView] = useState(false);

    const getTodayInIST = () => {
        const now = new Date();

        const istOffset = 5.5 * 60 * 60 * 1000;
        const istDate = new Date(now.getTime() + istOffset);

        return istDate.toISOString().split("T")[0];
    };

    const getTomorrowInIST = () => {
        const now = new Date();

        const istOffset = 5.5 * 60 * 60 * 1000;
        const istDate = new Date(now.getTime() + istOffset);

        istDate.setDate(istDate.getDate() + 1);
        return istDate.toISOString().split("T")[0];
    };

    const [selectedDate, setSelectedDate] = useState(date || getTodayInIST());

    const handleSelect = (option, type) => {
        if (type === "model") {
            setSelectedModel(option);

            switch (selectedOption) {
                case "Load Forecast":
                    setSelectedLoadModel(option);
                    localStorage.setItem("selectedLoadModel", option);
                    break;
                case "Price Forecast":
                    setSelectedPriceModel(option);
                    localStorage.setItem("selectedPriceModel", option);
                    break;
                case "Solar Forecast":
                    setSelectedSolarModel(option);
                    localStorage.setItem("selectedSolarModel", option);
                    break;
                case "Wind Forecast":
                    setSelectedWindModel(option);
                    localStorage.setItem("selectedWindModel", option);
                    break;
                default:
                    break;
            }
        }
        setOpenDropdown(null);
    };

    const handleSelects = (option, type) => {
        if (type === "market") {
            setSelectedOption(option);
        }
        setOpenDropdown(null);
    };

    const toggleDropdown = (type) => {
        setOpenDropdown(openDropdown === type ? null : type);
    };

    const convertBlockNoToTime = (blockNo) => {
        if (!blockNo || isNaN(blockNo)) return "-";
        const hour = String(Math.floor((blockNo - 1) / 4)).padStart(2, '0');
        const min = String(((blockNo - 1) % 4) * 15).padStart(2, '0');
        return `${hour}:${min}`;
    };

    useEffect(() => {
        if (date) {
            setSelectedDate(date);
        }
    }, [date]);

    useEffect(() => {
        if (timeRange === "0D") {
            const storedRates = JSON.parse(localStorage.getItem("mrRates")) || {};
            const rate = storedRates[selectedOption] ?? 0.2;
            setMrRate(rate);
            callMRRateAPI(rate, selectedDate);
        } else if (timeRange === "1D") {
            fetchForecastData(selectedDate);
        } else {
            const { from, to } = getDateRange(timeRange);
            fetchLoadForecastByDateRange(from, to);
        }
    }, [selectedOption, timeRange]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    useEffect(() => {
        if (statusMessage) {
            const timer = setTimeout(() => {
                setStatusMessage('');
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [statusMessage]);

    useEffect(() => {
        if (showEditModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [showEditModal]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        fetchAllForecastModels();
    }, [selectedOption]);

    const handleMRRateChange = (value) => {
        const roundedValue = value;
        setMrRate(roundedValue);

        const updatedRates = JSON.parse(localStorage.getItem("mrRates")) || {};
        updatedRates[selectedOption] = roundedValue;
        localStorage.setItem("mrRates", JSON.stringify(updatedRates));

        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }

        const timer = setTimeout(async () => {
            await callMRRateAPI(roundedValue, selectedDate);

            const forecastMap = {
                "Load Forecast": "demand",
                "Solar Forecast": "solar",
                "Wind Forecast": "wind",
            };

            const mappedKey = forecastMap[selectedOption];
            if (mappedKey) {
                const postBody = [[mappedKey, roundedValue]];
                await postMRRates(postBody);
            }
        }, 3000);

        setDebounceTimer(timer);
    };

    const callMRRateAPI = async (rate, date = getTodayInIST()) => {
        setLoading(true);

        try {
            const roundedRate = rate;
            const loadModel = selectedLoadModel || "";
            const priceModel = selectedPriceModel || "";
            const solarModel = selectedSolarModel || "";
            const windModel = selectedWindModel || "";
            const selectedApi = mrRateApis[selectedOption];
            setShowCommitButton(false);

            if (selectedApi) {
                try {
                    setHeaders([]);
                    setTableData([]);
                    setXData([]);
                    setYData([]);
                    let modelParam = "user_hybrid";
                    switch (selectedOption) {
                        case "Load Forecast":
                            modelParam = loadModel;
                            break;
                        case "Wind Forecast":
                            modelParam = windModel;
                            break;
                        case "Solar Forecast":
                            modelParam = solarModel;
                            break;
                        case "Price Forecast":
                            modelParam = priceModel;
                            break;
                    }

                    const response = await selectedApi(date, roundedRate, modelParam);
                    const data = response.data;
                    processJsonsData(data, date);
                } catch (err) {
                    //  console.error("API call failed:", err);
                    setHeaders([]);
                    setTableData([]);
                    setXData([]);
                    setYData([]);
                }
            }
        } catch (err) {
            console.error("Forecast API error:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        const { from, to } = getDateRange(timeRange);

        if (startDate && endDate) {
            handleApply();
        } else if (timeRange === "1D") {
            fetchForecastData(selectedDate);
        } else if (timeRange === "0D") {
            const storedRatesJson = localStorage.getItem("mrRates");
            const storedRates = storedRatesJson ? JSON.parse(storedRatesJson) : {};
            const defaultFallbackRate = 0.2;
            const rateFromStorage = storedRates[selectedOption];
            const rateToUse = typeof rateFromStorage === "number" ? rateFromStorage : defaultFallbackRate;
            setMrRate(rateToUse);
            callMRRateAPI(rateToUse, selectedDate);
        } else {
            fetchLoadForecastByDateRange(from, to);
        }
    };

    const fetchForecastData = async (date = getTomorrowInIST()) => {
        setLoading(true); // ✅ Start loading bar

        try {
            if (forecastApis[selectedOption]) {
                setShowCommitButton(true);
                try {
                    setHeaders([]);
                    setTableData([]);
                    setXData([]);
                    setYData([]);
                    const response = await forecastApis[selectedOption](date);
                    const data = response.data;

                    setForecastData(data);
                    processJsonData(data, date);

                    switch (selectedOption) {
                        case "Load Forecast":
                            setLoadForecastData(data);
                            break;
                        case "Price Forecast":
                            setPriceForecastData(data);
                            break;
                        case "Solar Forecast":
                            setSolarForecastData(data);
                            break;
                        case "Wind Forecast":
                            setWindForecastData(data);
                            break;
                        default:
                            break;
                    }
                } catch (error) {
                    //  console.error("Error fetching forecast:", error);
                    setHeaders([]);
                    setTableData([]);
                    setXData([]);
                    setYData([]);
                }
            }
        } catch (err) {
            console.error("Forecast API error:", err);
        } finally {
            setLoading(false);
        }
    };

    const processJsonData = (data, date) => {
        if (!Array.isArray(data) || data.length === 0) {
            //  console.error("Invalid or empty data format");
            return;
        }

        const uniqueHeaders = new Set(["Date", "Block No"]);
        const processedData = {};
        const columnDataTypes = {};
        const columnsWithNull = new Set();
        const nonEditableColumns = new Set(["Date", "Block No", "block_no"]);

        const normalizedSelectedDate = new Date(date).toISOString().split("T")[0];

        data.forEach((row) => {
            const { date, block_no, ...otherFields } = row;

            if (!date || !block_no) {
                //console.warn("Skipping row due to missing Date or Block No:", row);
                return;
            }

            const formattedDate = new Date(date).toISOString().split("T")[0];
            if (formattedDate !== normalizedSelectedDate) return;

            const keys = Object.keys(otherFields);
            if (keys.length < 2) {
                //   console.warn("Skipping row due to insufficient keys:", row);
                return;
            }

            const dynamicKey = keys.find(key => typeof row[key] === "string");
            const valueKey = keys.find(key => key !== dynamicKey);

            if (!dynamicKey || !valueKey) {
                //   console.warn("Skipping row due to missing dynamicKey or valueKey:", row);
                return;
            }

            const rowKey = `${formattedDate}-${block_no}`;
            if (!processedData[rowKey]) {
                processedData[rowKey] = { Date: formattedDate, "Block No": block_no };
            }

            const rawValue = row[valueKey];
            let finalValue = rawValue;

            if (rawValue === null) {
                columnsWithNull.add(row[dynamicKey]);
                finalValue = "null";
            } else if (!isNaN(rawValue)) {
                finalValue = Math.round(parseFloat(rawValue));
            }

            processedData[rowKey][row[dynamicKey]] = finalValue;
            uniqueHeaders.add(row[dynamicKey]);

            if (!columnDataTypes[row[dynamicKey]]) {
                if (rawValue === null) {
                    columnDataTypes[row[dynamicKey]] = "string";
                } else {
                    columnDataTypes[row[dynamicKey]] = isNaN(rawValue) ? "string" : "number";
                }
            }
        });

        const headersWithMetadata = Array.from(uniqueHeaders).map((header) => ({
            name: header,
            dataType:
                header === "Date"
                    ? "date"
                    : header === "Block No"
                        ? "number"
                        : columnDataTypes[header] || "string",
            editable: !nonEditableColumns.has(header),
            width: 150,
            displayName: header,
        }));

        const allRows = Object.values(processedData);

        const sortedData = allRows.sort((a, b) => (a["Block No"] || 0) - (b["Block No"] || 0));

        setHeaders(headersWithMetadata);
        setTableData(sortedData);
        prepareChartData(sortedData, Array.from(uniqueHeaders));
    };

    const processMultiJsonData = (data) => {
        setSummaryTable([]);

        if (!Array.isArray(data) || data.length === 0) {
            //   console.error("Invalid or empty data format");
            return;
        }

        const uniqueHeaders = new Set(["Date", "Block No"]);
        const processedData = {};
        const columnDataTypes = {};
        const nonEditableColumns = new Set(["Date", "Block No", "block_no"]);
        const columnsWithNull = new Set();

        data.forEach((row) => {
            let date = row["date"] || row["Date"] || row["datetime"];
            const block_no = row["block_no"] || row["Block No"];

            if (typeof date === "string" && date.includes("T")) {
                date = date.split("T")[0];
            }

            if (!date || !block_no) {
                // console.warn("Skipping row due to missing Date or Block No:", row);
                return;
            }

            const modelName = row["model_name"];
            if (!modelName) {
                // console.warn("Skipping row due to missing model_name:", row);
                return;
            }

            const valueKey = Object.keys(row).find(key =>
                !["date", "Date", "datetime", "block_no", "Block No", "model_name", "rev_no"].includes(key) &&
                !isNaN(parseFloat(row[key]))
            );

            if (!valueKey) {
                //  console.warn("Skipping row due to missing numeric value field:", row);
                return;
            }

            const rowKey = `${date}-${block_no}`;
            if (!processedData[rowKey]) {
                processedData[rowKey] = {
                    Date: date,
                    "Block No": block_no
                };
            }

            const rawValue = row[valueKey];
            let finalValue = rawValue;

            if (rawValue === null) {
                columnsWithNull.add(modelName);
                finalValue = "null";
            } else if (!isNaN(rawValue)) {
                finalValue = Math.round(parseFloat(rawValue));
            }

            processedData[rowKey][modelName] = finalValue;
            uniqueHeaders.add(modelName);

            if (!columnDataTypes[modelName]) {
                if (rawValue === null) {
                    columnDataTypes[modelName] = "string";
                } else {
                    columnDataTypes[modelName] = isNaN(rawValue) ? "string" : "number";
                }
            }
        });

        const headersWithMetadata = Array.from(uniqueHeaders).map((header) => ({
            name: header,
            dataType: header === "Date" ? "date" : header === "Block No" ? "number" : columnDataTypes[header] || "string",
            editable: !nonEditableColumns.has(header),
            width: 150,
            displayName: header,
        }));

        const sortedData = Object.values(processedData).sort((a, b) => {
            const dateA = new Date(a["Date"]);
            const dateB = new Date(b["Date"]);

            if (dateA < dateB) return -1;
            if (dateA > dateB) return 1;

            return (parseInt(a["Block No"]) || 0) - (parseInt(b["Block No"]) || 0);
        });

        setHeaders(headersWithMetadata);
        setTableData(sortedData);
        prepareSummaryTableData(sortedData);
        prepareChartsData(sortedData, Array.from(uniqueHeaders));
    };

    const processJsonsData = (data, date) => {
        if (!Array.isArray(data) || data.length === 0) {
            //  console.error("Invalid or empty data format");
            return;
        }

        const rtForecastMap = {};
        const uniqueHeaders = new Set(["Date", "Block No"]);
        const processedData = {};
        const columnDataTypes = {};
        const nonEditableColumns = new Set(["Date", "Block No", "block_no"]);
        const columnsWithNull = new Set();

        const normalizedSelectedDate = new Date(date).toISOString().split("T")[0];

        data.forEach((row) => {
            const { date, block_no, model_name, forecast } = row;

            if (!date || !block_no || !model_name) {
                return;
            }

            const formattedDate = new Date(date).toISOString().split("T")[0];
            if (formattedDate !== normalizedSelectedDate) return;

            let finalForecast = forecast;
            if (finalForecast === null) {
                columnsWithNull.add(model_name);
                finalForecast = "null";
            } else if (!isNaN(finalForecast)) {
                finalForecast = Math.round(parseFloat(finalForecast));
            }

            const rowKey = `${formattedDate}-${block_no}`;
            if (!processedData[rowKey]) {
                processedData[rowKey] = { Date: formattedDate, "Block No": block_no };
            }

            processedData[rowKey][model_name] = finalForecast;
            uniqueHeaders.add(model_name);

            if (model_name === "RT_FORECAST") {
                rtForecastMap[block_no] = finalForecast;
            }

            if (!columnDataTypes[model_name]) {
                columnDataTypes[model_name] = isNaN(finalForecast) ? "string" : "number";
            }
        });

        const headersWithMetadata = Array.from(uniqueHeaders).map((header) => ({
            name: header,
            dataType:
                header === "Date"
                    ? "date"
                    : header === "Block No"
                        ? "number"
                        : columnDataTypes[header] || "string",
            editable: !nonEditableColumns.has(header),
            width: 150,
            displayName: header,
        }));

        const allRows = Object.values(processedData);
        const sortedData = allRows.sort((a, b) => (a["Block No"] || 0) - (b["Block No"] || 0));

        setHeaders(headersWithMetadata);
        setTableData(sortedData);
        prepareChartData(sortedData, Array.from(uniqueHeaders));

        const currentTime = new Date();
        const currentHours = currentTime.getHours();
        const currentMinutes = currentTime.getMinutes();
        const currentBlock = currentHours * 4 + Math.floor(currentMinutes / 15) + 1;

        const maxBlocksPerDay = 96;
        const rtForecastHeaders = [];
        for (let i = 0; i < 26; i++) {
            const block = currentBlock + i;
            if (block > maxBlocksPerDay) break;
            rtForecastHeaders.push(block);
        }
        const rtForecastRow = rtForecastHeaders.map(block => rtForecastMap[block] !== undefined ? rtForecastMap[block] : "-");

        setRtForecastHeaders(rtForecastHeaders);
        setRtForecastRow(rtForecastRow);
    };

    const prepareSummaryTableData = (tableData) => {
        const grouped = {};

        tableData.forEach(row => {
            const date = row["Date"];
            const blockNo = row["Block No"];
            const modelKey = ["user_hybrid", "st_momentum"].find(key => key in row);

            if (!date || !blockNo || !modelKey) return;

            const val = row[modelKey];
            const numVal = val === "null" ? null : parseFloat(val);

            if (!grouped[date]) grouped[date] = [];
            if (!isNaN(numVal)) {
                grouped[date].push({ value: numVal, time: blockNo });
            }
        });

        const summary = Object.entries(grouped).map(([date, entries]) => {
            const values = entries.map(e => e.value);
            const max = Math.max(...values);
            const min = Math.min(...values);
            const avg = values.reduce((a, b) => a + b, 0) / values.length;
            const generation = values.reduce((a, b) => a + b, 0) / 4000;

            const maxTime = entries.find(e => e.value === max)?.time;
            const minTime = entries.find(e => e.value === min)?.time;

            return {
                date,
                max: max.toFixed(2),
                maxTime: convertBlockNoToTime(maxTime),
                min: min.toFixed(2),
                minTime: convertBlockNoToTime(minTime),
                avg: avg.toFixed(2),
                generation: generation.toFixed(2)
            };
        });

        setSummaryTable(summary);
    };

    const prepareChartData = (data, fields) => {
        if (data.length > 0 && fields.length > 2) {
            const generateTimeSlots = (blocks) => {
                return Array.from({ length: blocks }, (_, i) => {
                    const hours = String(Math.floor(i / 4)).padStart(2, "0");
                    const minutes = String((i % 4) * 15).padStart(2, "0");
                    return `${hours}:${minutes}`;
                });
            };

            const timeSlots = generateTimeSlots(96);

            const seenDates = new Set();
            const xValues = data.map((row) => {
                const time = timeSlots[row["Block No"] - 1];
                const dateStr = row["Date"];

                if (!seenDates.has(dateStr)) {
                    seenDates.add(dateStr);
                    const dateObj = new Date(dateStr);
                    const day = String(dateObj.getDate()).padStart(2, "0");
                    const month = dateObj.toLocaleString("default", { month: "short" });
                    return `${day} ${month}`;
                }

                return time;
            });

            const numericFields = fields.filter(
                (field) =>
                    field !== "Date" &&
                    field !== "Block No" &&
                    data.some(
                        (row) =>
                            !isNaN(parseFloat(row[field])) && isFinite(row[field])
                    )
            );

            const yValues = numericFields.map((field, index) => ({
                field,
                values: data.map((row) => parseFloat(row[field]) || 0),
                color: d3.schemeCategory10[index % 10],
            }));

            setXData(xValues);
            setYData(yValues);
        }
    };

    const prepareChartsData = (data, fields) => {
        if (data.length > 0 && fields.length > 2) {
            const generateTimeSlots = (blocks) => {
                return Array.from({ length: blocks }, (_, i) => {
                    const hours = String(Math.floor(i / 4)).padStart(2, "0");
                    const minutes = String((i % 4) * 15).padStart(2, "0");
                    return `${hours}:${minutes}`;
                });
            };

            const timeSlots = generateTimeSlots(96);

            const xValues = data.map((row) => {
                const dateStr = row["Date"];
                const blockNo = row["Block No"];
                const time = timeSlots[blockNo - 1] || "00:00";

                const dateObj = new Date(dateStr);
                const day = String(dateObj.getDate()).padStart(2, "0");
                const month = dateObj.toLocaleString("default", { month: "short" });

                return `${month} ${day} - ${time}`;
            });

            const numericFields = fields.filter(
                (field) =>
                    field !== "Date" &&
                    field !== "Block No" &&
                    data.some(
                        (row) =>
                            !isNaN(parseFloat(row[field])) && isFinite(row[field])
                    )
            );

            const yValues = numericFields.map((field, index) => ({
                field,
                values: data.map((row) => parseFloat(row[field]) || 0),
                color: d3.schemeCategory10[index % 10],
            }));

            setXData(xValues);
            setYData(yValues);
        }
    };

    const handleCommit = async () => {
        let updatedData;

        if (showTable) {
            updatedData = tableRef.current?.getUpdatedData?.();
        } else if (chartRef.current?.getUpdatedChartData) {
            updatedData = chartRef.current.getUpdatedChartData?.();
        } else {
            setStatusMessage("No method to fetch updated data from ref.");
            setMessageType("error");
            return;
        }
        if (!updatedData || updatedData.length === 0) {
            setStatusMessage("No data to commit.");
            setMessageType("error");
            return;
        }

        if (selectedOption === "Price Forecast") {
            return;
        }

        const formattedData = [];

        if (showTable) {
            updatedData.forEach((row) => {
                const value = row["user_hybrid"] === "null" ? null : row["user_hybrid"];
                formattedData.push(['user_hybrid', row.Date, row["Block No"], value]);
            });
        } else {
            updatedData.forEach((series) => {
                if (series.field === "user_hybrid") {
                    series.values.forEach((value, index) => {
                        const block_no = index + 1;
                        formattedData.push(['user_hybrid', selectedDate, block_no, value]);
                    });
                }
            });
        }

        if (!commitForecastApis[selectedOption]) {
            setStatusMessage("No API found for the selected option.");
            setMessageType("error");
            return;
        }

        try {
            const response = await commitForecastApis[selectedOption](formattedData);
            if (response.status === 200) {
                setStatusMessage("Data committed successfully!");
                setMessageType("success");
            } else {
                setStatusMessage("Failed to commit data.");
                setMessageType("error");
            }
        } catch (error) {
            //  console.error("Error updating forecast:", error);
            setStatusMessage("Error updating forecast.");
            setMessageType("error");
        }
    };

    const handleApply = () => {
        if (startDate && endDate) {
            setTimeRange("");
            fetchLoadForecastByDateRange(startDate, endDate);
            setShowButtons(false);
        }
    };

    const getDateRange = (range) => {
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);

        let fromDate;
        let toDate;

        switch (range) {
            case "0D":
                fromDate = new Date(today);
                toDate = new Date(today);
                break;
            case "1D":
                fromDate = new Date(tomorrow);
                toDate = new Date(tomorrow);
                break;
            case "2D":
                fromDate = new Date(tomorrow);
                toDate = new Date(tomorrow);
                toDate.setDate(toDate.getDate() + 1);
                break;
            case "7D":
                fromDate = new Date(tomorrow);
                toDate = new Date(tomorrow);
                toDate.setDate(toDate.getDate() + 6);
                break;
            case "14D":
                fromDate = new Date(tomorrow);
                toDate = new Date(tomorrow);
                toDate.setDate(toDate.getDate() + 13);
                break;
            case "1M":
                fromDate = new Date(tomorrow);
                toDate = new Date(tomorrow);
                toDate.setMonth(toDate.getMonth() + 1);
                break;
            case "2M":
                fromDate = new Date(tomorrow);
                toDate = new Date(tomorrow);
                toDate.setMonth(toDate.getMonth() + 2);
                break;
            case "1Y":
                fromDate = new Date(tomorrow);
                toDate = new Date(tomorrow);
                toDate.setFullYear(toDate.getFullYear() + 1);
                break;
            case "ST":
                fromDate = new Date(tomorrow);
                fromDate.setFullYear(fromDate.getFullYear() - 5);
                toDate = new Date(tomorrow);
                break;
            default:
                fromDate = new Date(tomorrow);
                toDate = new Date(tomorrow);
                break;
        }

        return {
            from: fromDate.toISOString().split("T")[0],
            to: toDate.toISOString().split("T")[0]
        };
    };

    const processMultiDateJsonData = (data) => {
        if (!Array.isArray(data) || data.length === 0) {
            //   console.error("Invalid or empty data format");
            return;
        }

        const allRows = [];
        const uniqueHeaders = new Set(["Date", "Block No"]);
        const columnDataTypes = {};
        const nonEditableColumns = new Set(["Date", "Block No", "block_no"]);

        const yDataMap = {};
        const xAxis = [];

        const generateTimeSlots = () =>
            Array.from({ length: 96 }, (_, i) => {
                const hours = String(Math.floor(i / 4)).padStart(2, "0");
                const minutes = String((i % 4) * 15).padStart(2, "0");
                return `${hours}:${minutes}`;
            });

        const timeSlots = generateTimeSlots();

        data.forEach((row) => {
            const { date, block_no, model_name, load_forecast } = row;
            if (!date || !block_no || !model_name) return;

            const value = parseFloat(load_forecast);
            if (isNaN(value)) return;

            const timeLabel = `${date} ${timeSlots[block_no - 1]}`;
            xAxis.push(timeLabel);

            const fieldKey = model_name;

            const rowData = {
                "Date": date,
                "Block No": block_no,
                [fieldKey]: Math.round(value),
            };
            allRows.push(rowData);
            uniqueHeaders.add(fieldKey);

            if (!yDataMap[fieldKey]) {
                yDataMap[fieldKey] = {
                    field: fieldKey,
                    values: [],
                    color: d3.schemeCategory10[Object.keys(yDataMap).length % 10],
                };
            }

            yDataMap[fieldKey].values.push(value);

            if (!columnDataTypes[fieldKey]) {
                columnDataTypes[fieldKey] = "number";
            }
        });

        const headersWithMetadata = Array.from(uniqueHeaders).map((header) => ({
            name: header,
            dataType:
                header === "Date"
                    ? "date"
                    : header === "Block No"
                        ? "number"
                        : columnDataTypes[header] || "string",
            editable: !nonEditableColumns.has(header),
            width: 150,
            displayName: header,
        }));

        const uniqueXAxis = [...new Set(xAxis)].sort();

        setHeaders(headersWithMetadata);
        setXData(uniqueXAxis);
        setYData(Object.values(yDataMap));
        setTableData(allRows);
    };

    const fetchLoadForecastByDateRange = async (fromDate, toDate) => {
        setLoading(true); // ✅ Start loading bar

        try {
            const fetchApi = forecastRangeApis[selectedOption];

            if (!fetchApi) {
                //console.warn(`No fetch API configured for selectedOption: ${selectedOption}`);
                return;
            }

            try {
                setHeaders([]);
                setTableData([]);
                setXData([]);
                setYData([]);
                const response = await fetchApi(fromDate, toDate);
                const jsonData = response.data;

                // const preparedData = processMultiDateJsonData(jsonData);
                // setForecastData(preparedData);
                processMultiJsonData(jsonData);
                setStartDate();
                setEndDate();
            } catch (err) {
                //  console.error(`Error fetching ${selectedOption} data:`, err);
            }
        } catch (err) {
            console.error("Forecast API error:", err);
        } finally {
            setLoading(false);
        }
    };

    const renderPopupContent = () => {
        switch (selectedOption) {
            case "Load Forecast":
                return (
                    <>
                        <label>For Date</label>
                        <input type="date" />

                        <label>lag-window</label>
                        <input type="number" />

                        <label>lamda weight</label>
                        <input type="number" />

                        <label>weekday_lag</label>
                        <input type="number" />

                        <label>neighbour_count</label>
                        <input type="number" />
                    </>
                );
            case "Price Forecast":
                return (
                    <>

                        <label>For Date</label>
                        <input type="date" />

                        <label>lag-window</label>
                        <input type="number" />

                        <label>lamda weight</label>
                        <input type="number" />

                        <label>weekday_lag</label>
                        <input type="number" />

                        <label>neighbour_count</label>
                        <input type="number" />


                        <label>Upper_limit</label>
                        <input type="number" />


                        <label>Lower_limit</label>
                        <input type="number" />
                    </>
                );
            case "Solar Forecast":
                return (
                    <>
                        <label>For Date</label>
                        <input type="date" />

                        <label>lag-window</label>
                        <input type="number" />

                        <label>lamda weight</label>
                        <input type="number" />

                        <label>neighbour_count</label>
                        <input type="number" />
                    </>
                );
            case "Wind Forecast":
                return (
                    <>
                        <label>For Date</label>
                        <input type="date" />

                        <label>lag-window</label>
                        <input type="number" />

                        <label>lamda weight</label>
                        <input type="number" />

                        <label>Median filter window</label>
                        <input type="number" />

                        <label>neighbour_count</label>
                        <input type="number" />
                    </>
                );
            default:
                return null;
        }
    };

    const handleAddRow = () => {
        if (!seriesName || !fromBlock || !toBlock || !weight) {
            alert("Please fill all fields before adding!");
            return;
        }

        const from = parseInt(fromBlock);
        const to = parseInt(toBlock);
        const wt = parseFloat(weight);

        if (isNaN(from) || isNaN(to) || isNaN(wt)) {
            alert("Please enter valid numbers!");
            return;
        }

        if (from > to) {
            alert("From should be <= To!");
            return;
        }

        const newRow = {
            series_name: seriesName,
            from_block: from,
            to_block: to,
            weight: wt
        };

        setRows(prevRows => [...prevRows, newRow]);

        setSeriesName("");
        setFromBlock("");
        setToBlock("");
        setWeight("");
    };

    const handleDeleteRow = (indexToDelete) => {
        setRows(prevRows => prevRows.filter((_, index) => index !== indexToDelete));
    };

    const handleSubmitCommit = async () => {
        if (rows.length === 0) {
            alert("Please fill all fields before adding!");
            return;
        }

        const userHybrid = {};
        const totalBlocks = tableData.length;
        const nonEditableColumns = new Set(["Date", "Block No", "block_no"]);

        for (let block = 1; block <= totalBlocks; block++) {
            userHybrid[block] = 0;
        }

        let hasInvalidInput = false;
        rows.forEach((row) => {
            const { series_name, from_block, to_block, weight } = row;
            const from = parseInt(from_block);
            const to = parseInt(to_block);
            const wt = parseFloat(weight);

            if (isNaN(from) || isNaN(to) || isNaN(wt)) {
                hasInvalidInput = true;
                return;
            }
            if (from < 1 || to > totalBlocks || from > to) {
                hasInvalidInput = true;
                return;
            }

            for (let block = from; block <= to; block++) {
                const dataRow = tableData.find(r => r["Block No"] === block);

                if (dataRow && !isNaN(dataRow[series_name])) {
                    userHybrid[block] += dataRow[series_name] * wt;
                }
            }
        });

        if (hasInvalidInput) {
            alert("Invalid block range or weight. Make sure From Block >= 1, To Block <= " + totalBlocks + " and From <= To.");
            return;
        }

        const hybridName = "user_hybrid";

        const updatedTable = tableData.map(row => {
            const block = row["Block No"];
            return {
                ...row,
                [hybridName]: userHybrid[block] || 0
            };
        });

        const allHeaders = Object.keys(updatedTable[0] || {});
        const headersWithMetadata = allHeaders.map((header) => ({
            name: header,
            dataType:
                header === "Date"
                    ? "date"
                    : header === "Block No" || header === hybridName
                        ? "number"
                        : "string",
            editable: !nonEditableColumns.has(header),
            width: 150,
            displayName: header,
        }));

        setTableData(updatedTable);
        setHeaders(headersWithMetadata);
        prepareChartData(updatedTable, headersWithMetadata.map(header => header.name));

        setRows([]);
        setSeriesName("");
        setFromBlock("");
        setToBlock("");
        setWeight("");
        setShowEditModal(false);
    };

    const handleCloseEditModal = () => {
        setRows([]);
        setSeriesName("");
        setFromBlock("");
        setToBlock("");
        setWeight("");
        setShowEditModal(false);
    };

    // const fetchAllForecastModels = async () => {
    //     try {
    //         fetchLoadModel()
    //             .then(response => {
    //                 const loadSet = new Set();
    //                 (response.data || []).forEach(item => {
    //                     if (item.model_name) loadSet.add(item.model_name);
    //                 });
    //                 setLoadModels(Array.from(loadSet));
    //             })
    //             .catch(error => {
    //                 //console.error("Load model fetch error:", error);
    //             });

    //         fetchSolarModel()
    //             .then(response => {
    //                 const solarSet = new Set();
    //                 (response.data || []).forEach(item => {
    //                     if (item.model_name) solarSet.add(item.model_name);
    //                 });
    //                 setSolarModels(Array.from(solarSet));
    //             })
    //             .catch(error => {
    //                 // console.error("Solar model fetch error:", error);
    //             });

    //         fetchWindModel()
    //             .then(response => {
    //                 const windSet = new Set();
    //                 (response.data || []).forEach(item => {
    //                     if (item.model_name) windSet.add(item.model_name);
    //                 });
    //                 setWindModels(Array.from(windSet));
    //             })
    //             .catch(error => {
    //                 //  console.error("Wind model fetch error:", error);
    //             });

    //         fetchPriceModel()
    //             .then(response => {
    //                 const priceSet = new Set();
    //                 (response.data || []).forEach(item => {
    //                     if (item.model_name) priceSet.add(item.model_name);
    //                 });
    //                 setPriceModels(Array.from(priceSet));
    //             })
    //             .catch(error => {
    //                 //   console.error("Price model fetch error:", error);
    //             });

    //     } catch (err) {
    //         //  console.error("Unexpected error:", err);
    //     }
    // };

    const fetchAllForecastModels = async () => {
        try {
            switch (selectedOption) {
                case "Load Forecast":
                    fetchLoadModel()
                        .then(response => {
                            const loadSet = new Set();
                            (response.data || []).forEach(item => {
                                if (item.model_name) loadSet.add(item.model_name);
                            });
                            setLoadModels(Array.from(loadSet));
                        });
                    break;
                case "Solar Forecast":
                    fetchSolarModel()
                        .then(response => {
                            const solarSet = new Set();
                            (response.data || []).forEach(item => {
                                if (item.model_name) solarSet.add(item.model_name);
                            });
                            setSolarModels(Array.from(solarSet));
                        });
                    break;
                case "Wind Forecast":
                    fetchWindModel()
                        .then(response => {
                            const windSet = new Set();
                            (response.data || []).forEach(item => {
                                if (item.model_name) windSet.add(item.model_name);
                            });
                            setWindModels(Array.from(windSet));
                        });
                    break;
                case "Price Forecast":
                    fetchPriceModel()
                        .then(response => {
                            const priceSet = new Set();
                            (response.data || []).forEach(item => {
                                if (item.model_name) priceSet.add(item.model_name);
                            });
                            setPriceModels(Array.from(priceSet));
                        });
                    break;
                default:
                    break;
            }
        } catch (err) {
            // console.error("Unexpected error:", err);
        }
    };

    const getSelectedModel = () => {
        switch (selectedOption) {
            case "Load Forecast":
                return selectedLoadModel;
            case "Price Forecast":
                return selectedPriceModel;
            case "Solar Forecast":
                return selectedSolarModel;
            case "Wind Forecast":
                return selectedWindModel;
            default:
                return "";
        }
    };

    return (
        <div className="positionmap-container" ref={dropdownRef}>
            {loading && (
                <div className="dashboard-loading-bar"></div>
            )}
            <div className="forescating-top-bar">
                <div className="forescating-top-bar-left">
                    {forecastType && forecastType.trim() !== "" && (
                        <AiOutlineFullscreenExit
                            style={{ cursor: "pointer", fontSize: "24px", marginBottom: "10px" }}
                            onClick={() => onExit(selectedDate)}
                            title="Back to Dashboard"
                        />
                    )}
                </div>

                <div className="forescating-top-bar-right">
                    <div className="date-picker">
                        <input
                            id="date-input"
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            placeholder=" "
                        />
                        <label htmlFor="date-input">Date</label>
                    </div>

                    <div className="dropdown-container">
                        <button
                            onClick={() => toggleDropdown("market")}
                            className="dropdown-button"
                        >
                            {selectedOption} {openDropdown === "market" ? <IoMdArrowDropup /> : <IoMdArrowDropdown />}
                        </button>

                        {openDropdown === "market" && (
                            <div className="dropdown-menus">
                                {options.map((option, index) => (
                                    <div
                                        key={index}
                                        onClick={() => handleSelects(option, "market")}
                                        className={`dropdown-item ${selectedOption === option ? "selected" : ""}`}
                                    >
                                        {option}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {statusMessage && (
                <div className={`status-message ${messageType}`}>
                    {statusMessage}
                </div>
            )}
            <div className="data-bar">
                <div className="left-section">
                    {selectedOption && (
                        <span
                            onClick={() => setShowTable(!showTable)}
                            className="toggle-view"
                        >
                            {showTable ? <FaChartBar size={24} /> : <FaTableCells size={24} />}
                        </span>
                    )}
                    <div className="dropdown-container">
                        <button
                            onClick={() => toggleDropdown("model")}
                            className="dropdown-button"
                        >
                            {getSelectedModel() || "Select model"}{" "}
                            {openDropdown === "model" ? <IoMdArrowDropup /> : <IoMdArrowDropdown />}
                        </button>

                        {openDropdown === "model" && (
                            <div className="dropdown-menus">
                                {(selectedOption === "Load Forecast"
                                    ? loadModels
                                    : selectedOption === "Price Forecast"
                                        ? priceModels
                                        : selectedOption === "Solar Forecast"
                                            ? solarModels
                                            : selectedOption === "Wind Forecast"
                                                ? windModels
                                                : []
                                ).map((option, index) => (
                                    <div
                                        key={index}
                                        onClick={() => handleSelect(option, "model")}
                                        className={`dropdown-item ${getSelectedModel() === option ? "selected" : ""
                                            }`}
                                    >
                                        {option}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>


                    <button className="optimize-button" onClick={() => setIsOpen(true)}>
                        <IoMdSettings className="button-icon" /> RUN SCORING
                    </button>

                </div>

                <div className="right-section">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label htmlFor="mrRate" style={{ minWidth: '80px' }}>MR Rate</label>
                        <input
                            type="range"
                            id="mrRate"
                            min="0"
                            max="1"
                            step="0.05"
                            value={mrRate}
                            onChange={(e) => handleMRRateChange(parseFloat(e.target.value))}
                            style={{ flexGrow: 1 }}
                        />
                        <span style={{ minWidth: '30px', textAlign: 'right' }}>{mrRate}</span>
                    </div>

                    {showCommitButton && (
                        <button className="commits-button" onClick={handleCommit}>
                            <MdCloudUpload className="button-icon" size={24} />
                        </button>
                    )}
                    <button className="edit-button" onClick={() => setShowEditModal(true)}>
                        <MdOutlineEdit className="button-icon" size={24} />
                    </button>

                    <button className="refresh-button" onClick={fetchData}>
                        <MdOutlineRefresh className="button-icon" size={24} />
                    </button>

                    {selectedOption === "Price Forecast" && (
                        <button
                            className="edit-button"
                            onClick={() => setIsStackedView(prev => !prev)}
                        >
                            {isStackedView ? (
                                <FaChartLine className="button-icon" size={24} />
                            ) : (
                                <AiOutlineAreaChart className="button-icon" size={24} />
                            )}
                        </button>
                    )}

                </div>

            </div>

            {(
                <div className="menu">
                    <div className="time-range-tabs">
                        {["0D", "1D", "2D", "7D", "14D", "1M", "2M", "1Y", "ST"].map(range => (
                            <div
                                key={range}
                                className={`tab ${timeRange === range ? "active-tab" : ""}`}
                                onClick={() => {
                                    setTimeRange(range);
                                    const { from, to } = getDateRange(range);
                                    setStartDate("");
                                    setEndDate("");
                                    if (range === "1D") {
                                        const tomorrow = getTomorrowInIST();
                                        setSelectedDate(tomorrow);
                                        fetchForecastData(tomorrow);
                                    } else if (range === "0D") {
                                        const storedRatesJson = localStorage.getItem("mrRates");
                                        const storedRates = storedRatesJson ? JSON.parse(storedRatesJson) : {};
                                        const rateToUse = typeof storedRates[selectedOption] === "number"
                                            ? storedRates[selectedOption]
                                            : 0.2;
                                        setMrRate(rateToUse);
                                        const today = getTodayInIST();
                                        setSelectedDate(today);
                                        callMRRateAPI(rateToUse, today);
                                    } else {
                                        fetchLoadForecastByDateRange(from, to);
                                    }
                                }}
                            >
                                {range}
                            </div>
                        ))}
                    </div>

                    <div className="time-apply-buttons" style={{ display: "flex", gap: "10px" }}>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            onFocus={() => setShowButtons(true)}
                        />
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            onFocus={() => setShowButtons(true)}
                        />
                    </div>

                    {showButtons && (
                        <div style={{ marginTop: "10px" }} className="time-apply-buttons">
                            <button onClick={handleApply} className="date-buttons">Apply</button>
                            <button className="date-buttons" onClick={() => {
                                setStartDate("");
                                setEndDate("");
                                setShowButtons(false);
                            }}>Cancel</button>
                        </div>
                    )}
                </div>

            )}

            {showTable ? (
                tableData.length > 0 && (
                    <Table
                        ref={tableRef}
                        headers={headers}
                         maxHeight="550px"
                        rows={tableData}
                        edit={true}
                        sorting={true}
                        drag={true}
                        filtering={true}
                        exporting={true}
                        addRow={false}
                        restrictEditToNewRows={false}
                    />
                )
            ) : (
                <>
                    {xData && xData.length > 0 && yData && yData.length > 0 && (
                        <>
                            {selectedOption === "Price Forecast" && isStackedView ? (
                                <StackedAreaChart
                                    xData={xData}
                                    yData={yData}
                                />
                            ) : (
                                <Charts
                                    ref={chartRef}
                                    xData={xData}
                                    yData={yData}
                                    drag={true}
                                    edit={true}
                                    forecastId={selectedOption.replace(/\s+/g, "_")}
                                />
                            )}

                            {timeRange === "0D" && rtForecastRow.length !== 0 && (
                                <div
                                    className="rt-forecast-table-container"
                                    style={{
                                        marginTop: "20px",
                                        overflowX: "auto",
                                        paddingRight: "18px",
                                        paddingLeft: "10px",
                                    }}
                                >
                                    <table
                                        className="rt-forecast-table"
                                        style={{ borderCollapse: "collapse", width: "100%" }}
                                    >
                                        <thead>
                                            <tr>
                                                <th
                                                    style={{
                                                        padding: "4px",
                                                        border: "1px solid #ccc",
                                                        backgroundColor: "#007BFF",
                                                        color: "white",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    Fcst
                                                </th>
                                                {rtForecastHeaders.map((blockNo, i) => {
                                                    const hours = String(Math.floor((blockNo - 1) / 4)).padStart(2, "0");
                                                    const minutes = String(((blockNo - 1) % 4) * 15).padStart(2, "0");
                                                    return (
                                                        <th
                                                            key={i}
                                                            style={{
                                                                padding: "4px",
                                                                border: "1px solid #ccc",
                                                                backgroundColor: "#007BFF",
                                                                color: "white",
                                                                textAlign: "center",
                                                            }}
                                                        >
                                                            {`${hours}:${minutes}`}
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td
                                                    style={{
                                                        padding: "4px",
                                                        border: "1px solid #ccc",
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {selectedOption === "Load Forecast"
                                                        ? "Demd"
                                                        : selectedOption === "Solar Forecast"
                                                            ? "Solar"
                                                            : selectedOption === "Wind Forecast"
                                                                ? "Wind"
                                                                : "Price"}
                                                </td>
                                                {rtForecastRow.map((val, idx) => (
                                                    <td
                                                        key={idx}
                                                        style={{
                                                            padding: "4px",
                                                            border: "1px solid #ccc",
                                                            textAlign: "center",
                                                        }}
                                                    >
                                                        {val}
                                                    </td>
                                                ))}
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {(selectedOption === "Load Forecast" ||
                                selectedOption === "Wind Forecast" ||
                                selectedOption === "Solar Forecast") &&
                                timeRange !== "0D" &&
                                timeRange !== "1D" &&
                                summaryTable.length > 0 && (
                                    <div
                                        style={{
                                            marginTop: "20px",
                                            overflowX: "auto",
                                            padding: "10px",
                                            maxWidth: "100vw",
                                        }}
                                    >
                                        <table className="summary-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    {summaryTable.map((row, idx) => (
                                                        <th key={idx}>{row.date}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[
                                                    "Expected Max [MW]",
                                                    "Time Block",
                                                    "Expected Min [MW]",
                                                    "Time Block",
                                                    "Average [MW]",
                                                    "Generation in [MUs]",
                                                ].map((label, rowIdx) => (
                                                    <tr key={rowIdx}>
                                                        <td className="label-cell">{label}</td>
                                                        {summaryTable.map((row, idx) => {
                                                            const val = [
                                                                row.max,
                                                                `${row.date} ${row.maxTime}`,
                                                                row.min,
                                                                `${row.date} ${row.minTime}`,
                                                                row.avg,
                                                                row.generation,
                                                            ][rowIdx];

                                                            const isBlue = label === "Generation in [MUs]";
                                                            return (
                                                                <td
                                                                    key={idx}
                                                                    className={isBlue ? "highlight-blue" : ""}
                                                                >
                                                                    {val}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                        </>
                    )}
                </>
            )}

            {/* <div className="dataImport-commit-container">
                <div
                    className="dataImport-commit"
                    onClick={handleCommit}
                >
                    COMMIT
                </div>
            </div> */}

            {isOpen && (
                <div className="popup-overlay" onClick={() => setIsOpen(false)}>
                    <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                        <div className="popup-header">
                            <h2>{selectedOption} Scoring</h2>
                            <button className="close-btn" onClick={() => setIsOpen(false)}>✖</button>
                        </div>

                        <div className="popup-body">
                            {renderPopupContent()}
                            <button className="run-btn">
                                <MdOutlineKeyboardArrowUp /> RUN SCORING
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showEditModal && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Edit Plot</h2>
                            <button className="close-modal-button" onClick={handleCloseEditModal}>✖</button>
                        </div>

                        <div className="inputs-area">
                            <select
                                className="input-box"
                                value={seriesName}
                                onChange={(e) => setSeriesName(e.target.value)}
                            >
                                <option value="">Select series</option>
                                {(selectedOption === "Load Forecast"
                                    ? loadModels
                                    : selectedOption === "Price Forecast"
                                        ? priceModels
                                        : selectedOption === "Solar Forecast"
                                            ? solarModels
                                            : selectedOption === "Wind Forecast"
                                                ? windModels
                                                : []
                                ).map((model, index) => (
                                    <option key={index} value={model}>
                                        {model}
                                    </option>
                                ))}
                            </select>

                            <input
                                className="input-box"
                                type="number"
                                placeholder="From block"
                                value={fromBlock}
                                onChange={(e) => setFromBlock(e.target.value)}
                            />
                            <input
                                className="input-box"
                                type="number"
                                placeholder="To block"
                                value={toBlock}
                                onChange={(e) => setToBlock(e.target.value)}
                            />
                            <input
                                className="input-box"
                                type="number"
                                placeholder="Weight"
                                value={weight}
                                onChange={(e) => setWeight(e.target.value)}
                            />

                            <button onClick={handleAddRow} className="add-button">
                                <GoPlusCircle className="button-icon" />

                            </button>
                        </div>

                        {rows.length > 0 && (
                            <div className="table-container">
                                <table className="edit-table">
                                    <thead>
                                        <tr>
                                            <th>Series Name</th>
                                            <th>From Block</th>
                                            <th>To Block</th>
                                            <th>Weight</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.series_name}</td>
                                                <td>{row.from_block}</td>
                                                <td>{row.to_block}</td>
                                                <td>{row.weight}</td>
                                                <td>
                                                    <button
                                                        className="delete-row-button"
                                                        onClick={() => handleDeleteRow(index)}
                                                    >
                                                        <MdDeleteForever className="button-icon" />

                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="commit-area" onClick={handleSubmitCommit}>
                            <button className="comit-button">Submit</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Forecasting;